import type {Messages} from './en';

const es: Messages = {
    languageSwitcher: {
        label: 'Idioma',
    },
    nav: {
        tagline: 'Kit de diseño de interfaces embebidas',
        logoAlt: 'Logotipo de Lopaka',
        gallery: 'Galería',
        journal: 'Blog',
        docs: 'Documentación',
        contactUs: 'Contacto',
        sourceCode: 'Código fuente',
        followTwitter: 'Síguenos en Twitter',
        followInstagram: 'Síguenos en Instagram',
        joinDiscord: 'Únete a Lopaka Hub en Discord',
        signIn: 'Iniciar sesión',
        projects: 'Proyectos',
    },
    legal: {
        cookies: 'Usamos cookies. {terms}, {privacy}',
        agreement: 'Al continuar, aceptas los {terms} y la {privacy} de Lopaka',
        terms: 'Términos del servicio',
        privacy: 'Política de privacidad',
    },
    meta: {
        home: {
            title: 'Editor gráfico y conversor de imágenes para Arduino, ESP32, Adafruit_GFX, u8g2, TFT_eSPI',
            description:
                'Editor de interfaces gráficas para pantallas embebidas, con generador de código pixel perfect y exportación. Ideal para Arduino, STM32 y ESP32. Convierte imágenes en variables uint32 y uint8 para Adafruit GFX, u8g2 y TFT_eSPI',
        },
        gallery: {
            title: 'Galería de proyectos de interfaz para pantallas embebidas — Lopaka',
            description:
                'Explora diseños de interfaz de la comunidad para pantallas OLED, TFT y de tinta electrónica. Abre cualquier proyecto en el editor de Lopaka y genera código para Arduino, ESP32 o STM32.',
        },
        price: {
            title: 'Precios y planes — Lopaka',
            description:
                'Compara el acceso gratuito a Lopaka con Plus Monthly y Plus Lifetime. Desbloquea fuentes personalizadas, paquetes de íconos, animaciones, proyectos privados y pantallas ilimitadas.',
        },
        app: {
            description:
                'Editor gráfico y conversor de imágenes en el navegador para pantallas embebidas con Arduino, ESP32 y STM32. Compatible con las bibliotecas Adafruit_GFX, u8g2 y TFT_eSPI.',
        },
        appPages: {
            projects: 'Tus proyectos — Lopaka',
            assets: 'Tus recursos — Lopaka',
            login: 'Iniciar sesión — Lopaka',
            loginExpired: 'Enlace de inicio de sesión vencido — Lopaka',
            upgrade: 'Mejora tu plan — Lopaka',
            success: 'Pago recibido — Lopaka',
        },
    },
    home: {
        hero: {
            headingLead: 'Kit de diseño de interfaces para',
            headingAccent: 'pantallas embebidas',
            taglineLead: 'Crea gráficos en minutos,',
            taglineAccent: 'no en días',
            taglineTail: 'con un editor visual + generador de código para microcontroladores',
            getStarted: 'Comenzar',
        },
        quickStart: {
            heading: 'Haz funcionar tu primera pantalla',
            intro: 'Parte de un diseño de pantalla real, ajústalo visualmente y exporta código listo para integrar en tu proyecto.',
            steps: {
                pick: 'Elige una pantalla y una biblioteca GFX',
                edit: 'Cambia textos, íconos o el diseño',
                export: 'Exporta el código y ejecútalo en tu hardware',
            },
            getStarted: 'Comenzar',
            browseTemplates: 'Ver plantillas',
            playVideo: 'Reproducir el video de inicio rápido de Lopaka',
            videoTitle: 'Video de inicio rápido de Lopaka',
            watchVideo: 'Mira el video para ver cómo funciona',
        },
        features: {
            heading: 'Interfaces embebidas sin complicaciones',
            tagline: 'Un conjunto completo de herramientas para crear gráficos que funcionan en microcontroladores.',
            shipFaster: {
                title: 'Lanza tu hardware más rápido',
                text: 'Genera automáticamente código listo para producción para cualquier microcontrolador, sin programarlo a mano. Concéntrate en el diseño, no en ajustar píxeles.',
            },
            confidence: {
                title: 'Diseña con confianza',
                text: 'Gráficos optimizados que se ven nítidos y claros en hardware de baja resolución. Lo que ves es exactamente lo que aparece en tu dispositivo.',
                imageAlt: 'Vista previa de la pantalla con precisión de píxel',
            },
            style: {
                title: 'Crea con estilo',
                text: 'Cientos de íconos optimizados para guiar a tus usuarios. Hechos a mano para pantallas pequeñas.',
                imageAlt: 'Biblioteca de íconos para pantallas embebidas',
            },
            readability: {
                title: 'Mejora la legibilidad',
                text: 'Accede a una biblioteca de fuentes pixel perfect diseñadas para leerse con claridad en pantallas embebidas. Importa fuentes TTF, OTF, BDF y GFX.',
            },
            platforms: {
                title: 'Usa cualquier plataforma',
                text: '{platforms} y más. Una sola herramienta para todos tus proyectos embebidos.',
            },
            projects: {
                title: 'Gestiona proyectos complejos',
                text: 'Pantallas con varias capas y varias pantallas físicas en un solo proyecto. Organiza tu interfaz con facilidad.',
                imageAlt: 'Gestión de proyectos con varias capas',
            },
            workflow: {
                title: 'Intégralo en tu flujo de trabajo',
                text: 'Exporta recursos en el formato exacto que requiere tu proyecto, desde C++ hasta mapas de bits sin procesar. Arreglos PNG, BMP y XBMP listos para sistemas embebidos.',
            },
            brand: {
                title: 'Mantén la coherencia de tu marca',
                text: 'Sube la tipografía de tu marca y tus propios recursos.',
                result: 'Míralos representados a la perfección en tu dispositivo.',
            },
        },
        templates: {
            heading: 'Cientos de plantillas',
            tagline: 'Creadas por la comunidad. Seleccionadas por nuestro equipo.',
            exploreMore: 'Ver más',
        },
        plansHeading: 'Empieza a crear hoy',
        community: {
            contribute: {
                title: 'Contribuye',
                text: 'Ayúdanos a construir el futuro de las interfaces embebidas. De código abierto e impulsado por la comunidad.',
                action: 'Ver en GitHub',
            },
            discord: {
                title: 'Únete a Discord Hub',
                text: 'Conecta con otros desarrolladores, comparte tus proyectos y recibe ayuda.',
                action: 'Unirme al servidor',
            },
            follow: {
                title: 'Síguenos',
                text: 'Mantente al día con las novedades, las funciones y los proyectos destacados de la comunidad.',
            },
        },
        rating: {
            label: 'Valoración de los usuarios',
            stars: '{rating} de 5 estrellas',
            summary: 'Valoración promedio basada en {count} respuestas a nuestra encuesta de usuarios',
        },
    },
    socialProof: {
        heading: 'Los comentarios lo dicen todo',
        label: 'Comentarios de Instagram',
        avatarAlt: 'Avatar de {username}',
    },
    mobileIntro: {
        heading: 'Continúa en una computadora',
        text: 'Lopaka está pensado como una aplicación de escritorio para ofrecerte la mejor experiencia.',
        close: 'Cerrar el aviso para continuar en una computadora',
        send: 'Envíame un enlace',
        sending: 'Enviando el enlace',
        sent: 'Te enviamos por correo un enlace para continuar en una computadora.',
        failed: 'No pudimos enviar el enlace. Inténtalo de nuevo.',
        watchGuide: 'Mira la guía de inicio rápido',
    },
    gallery: {
        tabs: {
            highlights: {title: 'Destacados', description: 'Seleccionados por el equipo de Lopaka'},
            trending: {title: 'Tendencias', description: 'Con más estrellas y copias'},
            favourite: {title: 'Favoritos', description: 'Tus estrellas'},
            all: {title: 'Todo', description: 'Todos los proyectos públicos'},
        },
        clearFilters: 'Borrar filtros',
        empty: 'No se encontraron proyectos en la galería.',
        showMore: 'Mostrar más',
        project: {
            screens: 'Pantallas: {count}',
            starThis: 'Dale una estrella',
            stars: 'Estrellas: {count}',
            forks: 'Copias: {count}',
        },
    },
    filters: {
        width: 'Ancho',
        height: 'Alto',
        all: 'Todas',
        allPlatforms: 'Todas las plataformas',
        search: 'Buscar',
        clearSearch: 'Borrar el filtro por nombre de proyecto',
    },
    pricing: {
        features: {
            codeGeneration: 'Generación de código',
            imageProcessing: 'Procesamiento de imágenes',
            communityFonts: 'Fuentes de la comunidad',
            communityIcons: 'Íconos de la comunidad',
            screens: '{count} pantallas',
            assetsStorage: 'Almacenamiento para {count} recursos',
            customFonts: 'Fuentes personalizadas',
            extraIcons: 'Más de 1500 íconos adicionales',
            publicProjects: '{count} proyectos públicos',
            screensPerProject: '{count} pantallas por proyecto',
            animations: 'Animaciones',
            exportOptions: 'Opciones de exportación',
            everythingInBasic: 'Todo lo de Basic y además:',
            unlimitedScreens: 'Pantallas ilimitadas',
            unlimitedAssets: 'Recursos ilimitados',
            privacySettings: 'Ajustes de privacidad',
            presentationMode: 'Modo presentación',
            teamCollaboration: 'Colaboración en equipo',
            customPlatformSupport: 'Soporte para plataformas personalizadas',
            apiAccess: 'Acceso a la API e integraciones',
            customFeatures: 'Funciones a medida',
            userManagement: 'SSO y gestión de usuarios',
            onPremise: 'Instalación en tus propios servidores',
            whiteLabel: 'Opciones de marca blanca',
            volumeLicensing: 'Licencias por volumen',
            dailyBackups: 'Copias de seguridad diarias',
            onboarding: 'Capacitación e incorporación',
            dedicatedSupport: 'Soporte dedicado',
        },
        upgradeHeading: 'Mejora tu plan para desbloquear las funciones de {plan}:',
        teamsDescription: 'Soluciones a medida para empresas de hardware, compañías de IoT y equipos de desarrollo',
        card: {
            free: 'GRATIS',
            save: 'Ahorra {amount}',
            comparedToMonthly: 'En comparación con el pago mensual',
            perYear: '/ año',
            perMonth: '/ mes',
            monthlyEquivalent: '~ {price}/mes',
            sevenDayPass: 'Pase de 7 días',
            lifetimeAccess: 'Acceso de por vida',
            oneTimePayment: 'Pago único',
            everythingInPlus: 'Todo lo de Plus y además:',
            currentPlan: 'Plan actual',
            signUp: 'Registrarme',
            requestQuote: 'Solicitar cotización',
        },
        actions: {
            buyNow: 'Comprar ahora',
            getWeekPass: 'Obtener acceso por 7 días',
            requestLifetime: 'Solicitar Lifetime',
            getLifetime: 'Obtener licencia de por vida',
            changeToAnnual: 'Cambiar a anual',
            changeToMonthly: 'Cambiar a mensual',
            changeToPlus: 'Cambiar a Plus',
            upgradeToPlus: 'Mejorar a Plus',
        },
        referral: {
            extraDays: '+ Recibes 7 días extra',
            discount: '+ Recibes un 10% de descuento',
        },
        education: {
            heading: 'Lopaka para el aula',
            question: '¿Eres docente y quieres usar Lopaka Plus con fines educativos?',
            offer: 'Ofrecemos {access} a los docentes que quieran usar Lopaka en sus clases.',
            access: 'acceso gratuito a Plus durante 1 año',
            contact: 'Contáctanos',
        },
    },
    auth: {
        signInTab: 'Iniciar sesión',
        registerTab: 'Crear cuenta nueva',
        or: 'O',
        continueWithGithub: 'Continuar con GitHub',
        continueWithGoogle: 'Continuar con Google',
        signOut: 'Cerrar sesión',
        email: 'Correo electrónico',
        password: 'Contraseña',
        passwordPlaceholder: 'Ingresa tu contraseña',
        newPassword: 'Nueva contraseña',
        newPasswordPlaceholder: 'Crea una nueva contraseña',
        confirmPassword: 'Confirmar contraseña',
        confirmPasswordPlaceholder: 'Repite tu nueva contraseña',
        forgotPassword: '¿Olvidaste tu contraseña?',
        backToSignIn: 'Volver a iniciar sesión',
        resetSent:
            'Abre el correo que enviamos a {email} y sigue el enlace de recuperación para elegir una nueva contraseña.',
        registerSent: 'Enviamos un enlace de confirmación a {email}',
        registerSentNext: 'Abre el correo para verificar tu cuenta y terminar de iniciar sesión.',
        modes: {
            signIn: {title: 'Inicia sesión en tu cuenta', submit: 'Iniciar sesión'},
            register: {title: 'Crea tu cuenta', submit: 'Crear cuenta'},
            forgotPassword: {title: 'Restablece tu contraseña', submit: 'Solicitar restablecimiento'},
            resetPassword: {title: 'Elige una nueva contraseña', submit: 'Actualizar contraseña'},
        },
        messages: {
            signingIn: 'Estamos iniciando tu sesión.',
            resetLinkSent: 'Enviamos un enlace para restablecer la contraseña a {email}.',
            passwordUpdated: 'Tu contraseña se actualizó.',
        },
        validation: {
            signInMissing: 'Ingresa tu correo electrónico y tu contraseña.',
            registerMissing: 'Ingresa tu correo electrónico y crea una contraseña.',
            accountExists: 'Ya existe una cuenta con este correo electrónico. Inicia sesión.',
            resetEmailMissing: 'Ingresa tu correo electrónico para restablecer la contraseña.',
            newPasswordMissing: 'Ingresa una nueva contraseña.',
            passwordsDiffer: 'Las contraseñas deben coincidir.',
        },
        errors: {
            invalid_credentials: 'El correo electrónico o la contraseña son incorrectos.',
            email_not_confirmed: 'Primero confirma tu correo electrónico: abre el enlace que te enviamos.',
            user_already_exists: 'Ya existe una cuenta con este correo electrónico. Inicia sesión.',
            email_exists: 'Ya existe una cuenta con este correo electrónico. Inicia sesión.',
            weak_password: 'Esta contraseña es demasiado débil. Elige una más larga.',
            same_password: 'La nueva contraseña debe ser distinta de la actual.',
            email_address_invalid: 'Ingresa un correo electrónico válido.',
            over_email_send_rate_limit: 'Se enviaron demasiados correos. Espera unos minutos e inténtalo de nuevo.',
            over_request_rate_limit: 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
        },
        expired: {
            title: 'El enlace para restablecer expiró',
            text: 'Este enlace para restablecer la contraseña no es válido o ya expiró. Solicita un nuevo correo e inténtalo de nuevo.',
            backToProjects: 'Volver a la lista de proyectos',
        },
        emailPlaceholder: 'tu{at}ejemplo.com',
    },
    notFound: {
        title: 'Página no encontrada',
        text: 'No pudimos encontrar la página que buscas. Es posible que se haya eliminado, que ahora sea privada o que la dirección sea incorrecta.',
        toGallery: 'Ir a la galería',
        toProjects: 'Mis proyectos',
    },
    appUpdate: {
        available: '¡Hay una nueva versión de Lopaka disponible!',
        update: 'Actualizar',
        loadFailed: 'No se pudo cargar la página',
        loadFailedText:
            'No se pudo cargar la página. Puede deberse a un problema de red, a un bloqueador de anuncios o a una actualización reciente de la aplicación.',
        reload: 'Recargar la página',
    },
    pageShell: {
        home: {
            exportTargets:
                'Diseña una pantalla en el navegador y exporta código fuente listo para compilar para {libraries}.',
            imageImport:
                'Importa PNG, BMP, GIF o video, y Lopaka convierte cada fotograma al formato que necesita tu pantalla: desde mapas de bits XBMP de 1 bit para OLED monocromáticas hasta arreglos RGB565 para TFT a color.',
            gallery: 'Explora la {galleryLink}, abre cualquier diseño en el editor y genera código a partir de él.',
            galleryLink: 'galería de proyectos',
            more: 'Lee la {docsLink} o compara {pricingLink}.',
            docsLink: 'documentación',
            pricingLink: 'planes y precios',
        },
        gallery: {
            heading: 'Galería de proyectos de interfaz para pantallas embebidas',
            intro: 'Diseños de interfaz de la comunidad para pantallas OLED, TFT y de tinta electrónica. Abre cualquier proyecto en el editor de Lopaka y genera código para Arduino, ESP32 o STM32.',
            more: 'Empieza tu propio diseño en el {editorLink} o lee la {docsLink}.',
            editorLink: 'editor de Lopaka',
            docsLink: 'documentación',
        },
        price: {
            heading: 'Precios y planes',
            intro: 'Compara los planes {plans} del editor de interfaces embebidas Lopaka, con facturación mensual o anual.',
            plans: {
                basic: 'De uso gratuito.',
                hobby: 'Para proyectos personales frecuentes.',
                plus: 'Para trabajar sin límites.',
                teams: 'Soluciones a medida para empresas de hardware, compañías de IoT y equipos de desarrollo.',
            },
            includes: 'Incluye {features}.',
            teachers:
                'Los docentes pueden solicitar acceso gratuito a Plus durante un año. Encontrarás los detalles de cada plan en la {docsLink}.',
            docsLink: 'documentación de planes',
        },
    },
    common: {
        cancel: 'Cancelar',
        save: 'Guardar',
        delete: 'Eliminar',
        close: 'Cerrar',
        back: 'Atrás',
        next: 'Siguiente',
        previous: 'Anterior',
        finish: 'Finalizar',
        done: 'Listo',
        apply: 'Aplicar',
        confirm: 'Confirmar',
        areYouSure: '¿Seguro?',
        edit: 'Editar',
        rename: 'Renombrar',
        duplicate: 'Duplicar',
        remove: 'Quitar',
        add: 'Agregar',
        copy: 'Copiar',
        copied: 'Copiado',
        download: 'Descargar',
        upload: 'Subir',
        search: 'Buscar',
        reset: 'Restablecer',
        retry: 'Intentar de nuevo',
        loading: 'Cargando…',
        saving: 'Guardando…',
        upgrade: 'Mejorar plan',
        upgradeNow: 'Mejorar ahora',
        public: 'Público',
        private: 'Privado',
        configure: 'Configurar',
        projectSettings: 'Ajustes del proyecto',
        selectAll: 'Seleccionar todo',
        deselectAll: 'Deseleccionar todo',
        none: 'Ninguno',
        yes: 'Sí',
        no: 'No',
        learnMore: 'Más información',
        unknownError: 'Algo salió mal. Inténtalo de nuevo.',
    },
    errors: {
        unauthenticated: 'Inicia sesión para administrar tus proyectos.',
        ownership_denied: 'Este proyecto o pantalla pertenece a otra cuenta.',
        not_found: 'No se encontró el proyecto o la pantalla. Es posible que se haya eliminado.',
        limit_reached: 'Alcanzaste el límite de tu plan. Mejora tu plan para agregar más.',
        invalid_input: 'Esta acción no es posible aquí.',
        read_failed: 'No se pudieron cargar tus datos. Revisa tu conexión e inténtalo de nuevo.',
        write_failed: 'No se pudieron guardar tus cambios. Revisa tu conexión e inténtalo de nuevo.',
        screenLimit: 'Alcanzaste el límite de pantallas de tu plan. Mejora tu plan para agregar más pantallas.',
        animationTooLarge:
            'La animación supera los 5 MiB. Reduce la cantidad de fotogramas o las dimensiones del lienzo antes de guardar.',
        animationUploadUnknown:
            'No pudimos confirmar que la animación se guardó. Revisa tu conexión y vuelve a guardar.',
        animationLastFrame: 'Una animación necesita al menos un fotograma.',
        assetLimit: 'Alcanzaste el límite de recursos subidos. Mejora tu plan para subir más.',
        signInToImportImages: 'Inicia sesión para importar imágenes.',
        signInToSaveAnimations: 'Inicia sesión para guardar animaciones.',
        screenCodeLoading: 'Espera a que cargue el código de la pantalla.',
        codeEditorClosed: 'El editor de código está cerrado.',
        readonly: 'Esta pantalla es de solo lectura.',
        stale_context: 'La pantalla cambió antes de que terminara la acción. Inténtalo de nuevo.',
        unsupported_for_platform: 'La plataforma actual no admite esto.',
        upgrade_required: 'Mejora tu plan para usar esto.',
        importOneVideo: 'Elige un solo archivo de video a la vez.',
        importVideoUnavailable: 'La importación de animaciones de video no está disponible en este editor.',
        importVideoUnreadable: 'No se pudo leer el contenedor del video seleccionado.',
        importVideoNoTrack: 'El archivo seleccionado no contiene una pista de video.',
        importVideoCodec: 'Este navegador no puede decodificar el códec de video del archivo seleccionado.',
        importVideoInvalid: 'El video seleccionado tiene una duración o dimensiones no válidas.',
        importVideoNoPreview: 'No se pudo decodificar ningún fotograma de vista previa del video.',
        importGifNoFrames: 'Selecciona al menos un fotograma del GIF para importar.',
        importGifFrameSize: 'Todos los fotogramas del GIF deben tener las mismas dimensiones procesadas.',
        importGifTooLarge: 'El GIF procesado supera el límite de tamaño o de fotogramas de la animación.',
    },
    projects: {
        heading: 'Mis proyectos',
        newProject: 'Nuevo proyecto',
        createHint: 'Crear un proyecto nuevo',
        upgradeHint: 'Mejora tu plan para agregar más proyectos',
        projectLimit: {
            title: 'Alcanzaste el límite de proyectos de tu plan actual.',
            text: 'Mejora tu plan ahora para desbloquear más proyectos y funciones avanzadas.',
        },
        screenLimit: {
            title: 'Alcanzaste el límite de pantallas de tu plan actual.',
            text: 'Mejora tu plan ahora para desbloquear más pantallas y funciones avanzadas.',
        },
        noFilterResults: 'No se encontraron proyectos con los filtros seleccionados.',
        resetFilters: 'Restablecer filtros',
        empty: 'Empieza creando un {newProject} o explora plantillas en la {gallery}',
        emptyNewProject: 'Nuevo proyecto',
        emptyGallery: 'Galería',
        community: {
            eyebrow: 'Comunidad',
            heading: 'Galería de proyectos',
            exploreMore: 'Explorar más',
        },
        favouritesOnly: 'Mostrar solo favoritos',
        card: {
            copy: 'Copiar este proyecto',
            screens: 'Pantallas: {count}',
        },
        title: {
            publicHint: 'Este proyecto es visible para todos los usuarios en la Galería',
            publicProject: 'Proyecto público',
            deleteHint: 'Eliminar el proyecto de forma permanente',
        },
        examples: {
            eyebrow: 'Inicio rápido',
            heading: 'Proyectos de ejemplo',
            open: 'Abrir en el editor',
            videoHeading: 'Video tutorial',
            videoTitle: 'Reproductor de video de YouTube',
            colorful: 'color',
            monochrome: 'monocromo',
            speedometer:
                'Tablero de vehículo con velocidad, RPM, odómetro, voltaje, combustible y estado del encendido',
            tirePressure: 'Lecturas en vivo de las cuatro ruedas sobre un diagrama del auto visto desde arriba',
        },
        wizard: {
            editTitle: 'Editar proyecto',
            createTitle: 'Crear proyecto nuevo',
            platform: 'Plataforma',
            title: 'Título',
            titlePlaceholder: 'Ingresa el título del proyecto',
            screenSize: 'Tamaño de pantalla',
            swap: 'Intercambiar dimensiones',
            selectSize: 'Selecciona el tamaño de pantalla',
            custom: 'Personalizado',
            rounded: 'Redonda',
            roundedHint: 'Pantalla redonda: ancho = alto, máscara circular en el lienzo',
            background: 'Color de fondo',
            backgroundHex: 'Color de fondo en hexadecimal',
            publish: 'Publicar en la Galería',
            publicHint: 'Los proyectos públicos son visibles para todos los usuarios',
            upgradeHint: 'Obtén pantallas ilimitadas y ajustes de privacidad',
            deleteHint: 'Elimina este proyecto y sus pantallas de forma permanente. No se puede deshacer.',
            createPublic: 'Crear proyecto público',
            createFailed: 'No se pudo crear el proyecto.',
            saveFailed: 'No se pudo guardar el proyecto.',
            deleteFailed: 'No se pudo eliminar el proyecto.',
            defaultTitle: 'Proyecto {number}',
        },
        copyFailed: 'No se pudo copiar el proyecto.',
    },
    animationEditor: {
        topBar: {
            frameCounter: 'Fotograma {current} / {total}',
            cancelHint: 'Se perderán todos los cambios de la animación que no hayas guardado.',
        },
        timeline: {
            deleteFrame: 'Eliminar fotograma seleccionado',
            firstFrame: 'Ir al primer fotograma',
            previousFrame: 'Fotograma anterior',
            play: 'Reproducir animación',
            pause: 'Pausar animación',
            nextFrame: 'Fotograma siguiente',
            lastFrame: 'Ir al último fotograma',
            duplicateFrame: 'Duplicar fotograma',
            addFrame: 'Agregar fotograma',
            animationColor: 'Color de la animación',
            totalLength: 'Duración total',
            durationSeconds: '{seconds} s',
            fpsHint: 'Fotogramas por segundo, de 1 a 60',
            fps: 'Fotogramas por segundo',
            fpsValue: '{fps} FPS',
            resizeCanvas: 'Cambiar tamaño del lienzo',
            estimatedSize: 'Tamaño estimado',
            onionSkinHint: 'Mostrar fotograma anterior',
            onionSkin: 'Papel cebolla',
            onion: 'Cebolla',
            frame: 'Fotograma {number}',
            dropCopy: '+ Copia',
            dropMove: 'Mover',
        },
        resizeDialog: {
            title: 'Cambiar tamaño del lienzo de la animación',
            description: 'Los píxeles quedan anclados en la esquina superior izquierda y no se escalan.',
            width: 'Ancho',
            height: 'Alto',
            resize: 'Cambiar tamaño',
            close: 'Cerrar el diálogo de tamaño',
        },
        errors: {
            previewFailed: 'No se pudo mostrar la vista previa de la animación.',
            frameLoadFailed: 'No se pudo cargar el fotograma.',
            operationFailed: 'No se pudo completar la acción en la animación.',
            playbackFailed: 'No se pudo iniciar la reproducción de la animación.',
            flattenFailed: 'No se pudo aplanar el fotograma actual.',
            imageAddFailed: 'No se pudo agregar la imagen.',
            animationLoadFailed: 'No se pudo cargar la animación.',
            animationAddFailed: 'No se pudo agregar la animación.',
            assetUnavailable:
                'El recurso de animación no está disponible. En su lugar, se abrió una animación editable en blanco.',
        },
    },
    inspector: {
        selectedLayers: 'Capas seleccionadas ({count})',
        labels: {
            font: 'Fuente',
            fontSize: 'Tamaño',
            inverted: 'Invertido (XOR)',
            fill: 'Relleno',
            color: 'Color de relleno',
            overlay: 'Superposición',
            alphaChannel: 'Transparencia',
            radius: 'R',
            rx: 'RX',
            ry: 'RY',
            w: 'An',
            h: 'Al',
            backgroundColor: 'Fondo',
            borderColor: 'Borde',
            borderWidth: 'B',
            checked: 'Marcado',
            rotation: 'Giro',
            startAngle: 'Inicio',
            endAngle: 'Fin',
            value: 'Valor',
            width: 'Ancho',
            smooth: 'Suave',
        },
        labelHints: {
            radius: 'Radio',
            rx: 'Radio horizontal',
            ry: 'Radio vertical',
            w: 'Ancho',
            h: 'Alto',
            backgroundColor: 'Color de fondo',
            borderColor: 'Color del borde',
            borderWidth: 'Grosor del borde',
            rotation: 'Rotación',
            startAngle: 'Ángulo inicial',
            endAngle: 'Ángulo final',
        },
        tooltips: {
            alphaChannel:
                'Mantiene transparentes los píxeles transparentes. Desactivado: se rellenan con el fondo de la pantalla',
            overlay: 'Atenúa la capa y la excluye de la generación de código',
        },
        textPlaceholder: 'Ingresa texto…',
        hexColor: 'Color hexadecimal de {label}',
        addVariable: 'Agregar variable',
        removeVariable: 'Quitar variable',
        rotateText: 'Girar texto {degrees}°',
        mergeToBitmap: 'Combinar en mapa de bits',
        mergeHint: 'Combinar las capas en un solo mapa de bits',
        editAnimationHint: 'Editar fotogramas de la animación',
        mergeFailed: 'No se pudieron combinar las capas.',
        mergeTooLarge: 'La imagen combinada es demasiado grande. Acerca más las capas seleccionadas.',
        mergeNothingSelected: 'Selecciona capas para combinar.',
        fontLoadFailed: 'No se pudo cargar la fuente. Comprueba que siga el formato GFX o BDF.',
        align: {
            left: 'Alinear a la izquierda',
            centerH: 'Centrar horizontalmente',
            right: 'Alinear a la derecha',
            top: 'Alinear arriba',
            centerV: 'Centrar verticalmente',
            bottom: 'Alinear abajo',
        },
        imageActions: {
            flipH: 'Voltear horizontalmente',
            flipV: 'Voltear verticalmente',
            rotate: 'Girar en sentido horario',
            invert: 'Invertir colores',
            trimPadding: 'Quitar márgenes vacíos',
            download: 'Descargar imagen',
            saveAsset: 'Agregar a los recursos del proyecto',
            saveAssetLabel: 'Guardar recurso',
            imageSaved: 'La imagen se guardó en los recursos del proyecto.',
        },
        colorInput: {
            noBackground: 'Sin fondo',
            clearBackground: 'Quitar fondo',
        },
        colors: {
            red: 'Rojo',
            pink: 'Rosa',
            purple: 'Morado',
            deepPurple: 'Morado oscuro',
            indigo: 'Índigo',
            blue: 'Azul',
            lightBlue: 'Azul claro',
            cyan: 'Cian',
            teal: 'Verde azulado',
            green: 'Verde',
            lightGreen: 'Verde claro',
            lime: 'Lima',
            yellow: 'Amarillo',
            amber: 'Ámbar',
            orange: 'Naranja',
            deepOrange: 'Naranja oscuro',
            brown: 'Marrón',
            blueGrey: 'Gris azulado',
        },
    },
    paint: {
        selectBrushColor: 'Seleccionar color de pincel {color}',
        paintColor: 'Color de pintura',
        brushSize: 'Tamaño del pincel',
        mono: 'Mono',
        cropImage: 'Recortar imagen',
        applyCropHint: 'Aplicar recorte (Enter)',
        cancelCropHint: 'Cancelar recorte (ESC)',
        eraser: 'Borrador',
        fill: 'Rellenar',
        patternHint: 'Forma y patrón del pincel',
        sizeLimit:
            'Esta capa de pintura alcanzó su tamaño máximo de {pixels} píxeles. Crea una nueva capa de pintura para dibujar más lejos.',
        patterns: {
            full: 'Cuadrado',
            circle: 'Círculo',
            dots: 'Puntos',
            dots2: 'Puntos (2px)',
            checkerboard: 'Tablero de ajedrez',
            checkerboardReverse: 'Tablero de ajedrez (inverso)',
            grid: 'Cuadrícula',
            horizontal1: 'Líneas horizontales (1px)',
            horizontal2: 'Líneas horizontales (2px)',
            vertical1: 'Líneas verticales (1px)',
            vertical2: 'Líneas verticales (2px)',
            diagonal2: 'Diagonal (2px)',
            diagonal2Reverse: 'Diagonal inversa (2px)',
            diagonal3: 'Diagonal (3px)',
            diagonal3Reverse: 'Diagonal inversa (3px)',
        },
    },
    fonts: {
        importFont: 'Importar fuente…',
        deleteFont: 'Eliminar fuente',
        fontInUse: 'No se puede eliminar una fuente que se usa en el proyecto actual',
        readFailed: 'No se pudo leer este archivo de fuente.',
        wizard: {
            previewTitle: 'Vista previa de la fuente: {name}',
            previewZoom: 'Zoom de la vista previa',
            fontHeight: 'Altura de la fuente',
            previewText: 'Texto de vista previa',
            import: 'Importar',
        },
    },
    screens: {
        addScreen: 'Agregar pantalla',
        upgradeToAdd: 'Mejora tu plan para agregar más pantallas',
        fallbackTitle: 'Pantalla',
        creating: 'Creando pantalla',
        deleting: 'Eliminando pantalla',
        hiddenInProjectSettings: 'Oculta en los ajustes del proyecto',
        actions: {
            publish: 'Publicar',
            makePublic: 'Hacer pública',
            makePrivate: 'Hacer privada',
            unlimitedHint: 'Mejora tu plan para tener pantallas ilimitadas',
            privacyHint: 'Mejora tu plan para tener privacidad',
        },
        carousel: {
            open: 'Abrir {project} en Canvas Flow',
            previewAlt: '{project}: pantalla {number}',
            screenTitle: 'Pantalla {number}',
            previewUnavailable: 'Vista previa no disponible',
            emptyCanvas: 'Lienzo vacío',
            previous: 'Pantalla anterior',
            next: 'Pantalla siguiente',
        },
    },
    flow: {
        layers: 'Capas',
        screensLeft: 'Te queda {count} pantalla gratis. | Te quedan {count} pantallas gratis.',
        seePlans: 'Ver planes que ofrecen más',
        canvasLabel: 'Lienzo del proyecto',
        imageSaved: 'Imagen guardada correctamente',
        view: {
            fitAll: 'Ajustar todo',
            fitSelection: 'Ajustar a la selección',
            hideInspector: 'Ocultar inspector',
            showInspector: 'Mostrar inspector',
        },
        liveView: {
            connect: 'Vista en vivo',
            disconnect: 'Desconectar',
            hint: 'Previsualiza tu diseño en tiempo real en tu Flipper por USB',
            noDevice: 'No se encontró ningún Flipper. Conecta tu Flipper por USB e inténtalo de nuevo.',
            disconnected:
                'Se desconectó la vista en vivo del Flipper. Vuelve a conectar tu Flipper por USB e inténtalo de nuevo.',
        },
        code: {
            tabs: 'Código de la pantalla',
            copyHint: 'Copiar código fuente',
            empty: 'Crea una pantalla para generar código.',
            importHint: 'Importar código (experimental): funciona mejor con código generado en Lopaka',
            loadFailed: 'No se pudo cargar el código de la pantalla.',
            generateFailed: 'No se pudo generar el código de esta pantalla.',
            importFailed: 'No se pudo importar el código.',
            copyFailed: 'No se pudo copiar el código al portapapeles.',
        },
        animation: {
            title: 'Animación',
            discardChanges: '¿Descartar los cambios no guardados de la animación?',
        },
        errors: {
            openFailed: 'No se pudo abrir el proyecto.',
            screenAddFailed: 'No se pudo agregar la pantalla. Inténtalo de nuevo.',
            screenUpdateFailed: 'No se pudo actualizar la pantalla.',
            imageLoadFailed: 'No se pudo cargar una imagen del proyecto.',
            assetsLoadFailed: 'No se pudieron cargar algunos recursos del proyecto.',
            unsavedKeepOpen: 'Tus últimos cambios no se guardaron. Mantén esta pestaña abierta.',
            unsaved: 'Tus últimos cambios no se guardaron.',
            previewFailed: 'No se pudo actualizar la vista previa de la pantalla.',
            imageUploadFailed: 'No se pudo subir la imagen {name}.',
            imagesImportFailed: 'No se pudieron importar las imágenes.',
            assetAddFailed: 'No se pudo agregar el recurso.',
            imageSaveFailed: 'No se pudo guardar la imagen.',
            animationOpenFailed: 'No se pudo abrir la animación.',
            animationSaveFailed: 'No se pudo guardar la animación.',
            documentUpdateFailed: 'No se pudo aplicar el cambio en el lienzo.',
        },
    },
    platforms: {
        picker: {
            placeholder: 'Selecciona una plataforma',
            setup: 'Configuración: {hint}',
            memory: 'Memoria: {hint}',
            noDescription: 'No hay descripción disponible.',
            github: 'Ver en GitHub',
            mono: 'Mono',
            easy: 'Fácil',
            hard: 'Difícil',
        },
        details: {
            tftEspi: {
                description:
                    'Una sola API de dibujo para los paneles SPI a color: ILI9341, ST7789, GC9A01, etc. El driver, los pines y la velocidad SPI se definen en el propio User_Setup.h de la biblioteca, así que la biblioteca se configura antes de compilar tu sketch, no desde tu código. M5GFX y LovyanGFX ofrecen las mismas llamadas. Sin widgets ni redibujado automático: dibujas cuando tú lo decides. El código se exporta como Arduino C++ o ESP-IDF C++.',
                sizeHint: 'Requiere ESP32, ESP8266, RP2040 o STM32',
                setupHint: 'Driver y pines definidos en User_Setup.h',
            },
            u8g2: {
                description:
                    'La opción predeterminada para paneles OLED y LCD monocromáticos como SSD1306, SH1106 y ST7565, donde cada píxel simplemente está encendido o apagado. Su lista de fuentes llega a cientos de variantes BDF, que suele ser la razón para elegirla, y el tamaño de la fuente viene del archivo de fuente, no de un factor de escala. El código se exporta como Arduino C++ o ESP-IDF C.',
                sizeHint: 'Con búfer por páginas funciona en un Arduino Uno',
                setupHint: 'Una línea de constructor, sin archivo de configuración',
                monoHint: 'Solo monocromático: un bit por píxel, sin color',
            },
            adafruitGfx: {
                description:
                    'La base de dibujo común bajo la mayoría de los drivers de pantalla de Adafruit, por eso hay ejemplos y tutoriales en todas partes. La combinas con el driver de tu panel exacto, como Adafruit_ST7789 o Adafruit_ILI9341, y ese driver se encarga de los pines y la inicialización. Los colores van empaquetados en RGB565 y el texto usa los encabezados de fuentes GFX. El dibujo va directo al panel sin framebuffer, así que incluso las placas de 8 bits pueden manejar una pantalla a color. El código se exporta como Arduino C++ o ESP-IDF C++.',
                sizeHint: 'Sin framebuffer; funciona en placas de 8 bits',
            },
            adafruitGfxMono: {
                description:
                    'Las mismas llamadas y fuentes GFX sobre una pantalla de un bit, manejada por Adafruit_SSD1306 o Adafruit_SH110X. El driver guarda toda la pantalla en un búfer de RAM y la envía con display(), lo que ocupa aproximadamente 1 KB en un panel de 128x64. Para una colección de fuentes mucho mayor en el mismo hardware, usa U8g2. El código se exporta como Arduino C++ o ESP-IDF C++.',
                sizeHint: 'Funciona en placas de 8 bits como Arduino Uno',
                setupHint: 'Instala el driver y ejecuta su ejemplo',
                monoHint: 'Solo monocromático: un bit por píxel, sin color',
            },
            lvgl: {
                description:
                    'Un toolkit de interfaz gráfica más que una biblioteca de dibujo: botones, sliders, interruptores, casillas y áreas de texto son objetos reales con sus propios estilos y manejo de eventos. Portarlo implica un lv_conf.h y un callback de flush que entrega los píxeles renderizados al driver de tu pantalla, así que espera algo de configuración antes de ver el primer píxel. Necesita un microcontrolador de 32 bits con RAM de sobra.',
                sizeHint: 'Necesita una placa de 32 bits con RAM de sobra',
                setupHint: 'Requiere lv_conf.h y un callback de flush',
            },
            arduinoGfx: {
                description:
                    'Una versión más reciente de la API GFX en la que el objeto de pantalla se arma en el sketch a partir de un bus de datos y una clase de panel, en lugar de editar un archivo de configuración de la biblioteca. Además de las primitivas comunes, agrega sus propias funciones de mapa de bits, incluida una variante big-endian para flujos de bytes que llegan con el byte alto primero. Pensada para placas de 32 bits más rápidas, incluidos paneles paralelos y RGB en ESP32-S3. El código se exporta como Arduino C++ o ESP-IDF C++.',
                sizeHint: 'Optimizada para placas de 32 bits como ESP32',
            },
            esphome: {
                description:
                    'Firmware para ESP32 y ESP8266 descrito en un solo archivo YAML: ESPHome lo compila y lo flashea por ti, y el dispositivo aparece en Home Assistant. El dibujo va dentro del lambda del componente display, mientras que las fuentes y las imágenes son claves YAML de nivel superior separadas, no parte de ese lambda. Las fuentes se pueden traer directamente de Google Fonts por URL. Las imágenes se exportan solo como XBMP de 1 bit.',
                sizeHint: 'Firmware completo; requiere ESP32 o ESP8266',
                setupHint: 'Solo YAML; ESPHome compila y flashea',
            },
            micropython: {
                description:
                    'Python ejecutándose en la propia placa: copias un archivo .py y se ejecuta, sin toolchain ni paso de compilación. El dibujo usa el módulo integrado framebuf a través del driver MicroPython-SSD1306 de PerfecXX, el único archivo que hay que copiar a RP2040, ESP32 y placas similares. Es solo de un bit, con una única fuente fija de 8x8 y sin esquinas redondeadas, lo que va mejor con lecturas de datos y menús simples que con diseños detallados. Aquí los triángulos son solo de contorno.',
                sizeHint: 'Requiere una placa con MicroPython, RP2040 o ESP32',
                setupHint: 'Copia un archivo .py a la placa',
                monoHint: 'Solo monocromático: un bit por píxel, sin color',
            },
            circuitpython: {
                description:
                    'La variante de Python de Adafruit: la placa se monta como unidad USB y tu código se vuelve a ejecutar en cuanto guardas el archivo. Los gráficos pasan por displayio y vectorio, que componen grupos de objetos de pantalla y los refrescan continuamente, en lugar de que tú envíes píxeles en cada fotograma. Los colores son hexadecimales de 24 bits, el texto usa la fuente BDF Terminus incluida y las imágenes se envían como búferes de bytes sin procesar. Necesitas una placa con una versión de CircuitPython compatible con tu pantalla.',
                sizeHint: 'Requiere una placa con CircuitPython y displayio',
                setupHint: 'La placa se monta como unidad USB',
            },
            flipper: {
                description:
                    'Dibuja en la pantalla de Flipper Zero y Flipper One mediante la API canvas del firmware, así que el resultado va dentro de una aplicación de Flipper creada con el SDK de apps del firmware. Solo cuatro fuentes fijas.',
                monoHint: 'Solo monocromático: un bit por píxel, sin color',
                setupHint: 'Se compila con el SDK de apps de Flipper',
            },
            gxepd2: {
                description:
                    'Para paneles de tinta electrónica (e-paper). Se basa en Adafruit GFX, así que las llamadas de dibujo y las fuentes son las conocidas, pero debes indicar la clase exacta de tu panel en una larga lista donde importa el número de modelo del cable plano. El color es un conjunto fijo de nueve constantes como GxEPD_BLACK y GxEPD_RED, y cualquier otro color se convierte en negro.',
                sizeHint: 'Los búferes del panel son grandes; la paginación ayuda',
                setupHint: 'Elige la clase exacta del panel',
            },
            freestyle: {
                description:
                    'Un lienzo simple sin biblioteca de destino: color RGB completo, todas las fuentes de Lopaka y nada de código generado. Úsalo para bosquejar un diseño o maquetar una pantalla antes de decidir el hardware, o simplemente para exportar una imagen.',
            },
        },
    },
    account: {
        menu: {
            assets: 'Recursos',
            plusAccessDaysLeft: 'Acceso Plus: queda {count} día | Acceso Plus: quedan {count} días',
            subscriptionDaysLeft: 'Suscripción: queda {count} día | Suscripción: quedan {count} días',
        },
        referral: {
            invite: 'Invitar a un amigo',
            getFreePlus: 'Obtén Plus gratis',
        },
        redirecting: 'Redirigiendo…',
        cancelFlow: {
            adBlocked:
                'Tu bloqueador de anuncios impide cancelar la suscripción. Desactívalo para lopaka.app e inténtalo de nuevo.',
            paddleUnavailable:
                'No se pudo abrir el proceso de cancelación de Paddle. Inténtalo de nuevo o contacta con soporte.',
            unavailable: 'No se pudo abrir la cancelación de la suscripción. Contacta con soporte.',
        },
        success: {
            heading: 'Procesando tu pago',
            wait: 'Espera mientras verificamos tu suscripción.',
            moments: 'Normalmente tarda unos instantes.',
            checking: 'Comprobando el estado de la suscripción…',
            progress: '{percent} completado',
            help: 'Escribe a {email} si sigues teniendo problemas',
        },
    },
    subscription: {
        manage: 'Gestionar suscripción',
        cancel: 'Cancelar suscripción',
        cycles: {
            weekly: 'Semanal',
            monthly: 'Mensual',
            yearly: 'Anual',
            lifetime: 'De por vida',
        },
        upgradeModal: {
            title: 'Mejorar suscripción',
            intro: 'Estás a punto de solicitar el cambio de {current} a {target}.',
            currentPlan: 'Plan actual:',
            requestedPlan: 'Plan solicitado:',
            requestedCycle: 'Ciclo de facturación solicitado:',
            renewalDate: 'Fecha de renovación de la suscripción actual:',
            nextRenewal: 'Próxima fecha de renovación:',
            newPrice: 'Nuevo precio tras el cambio:',
            manualNote:
                'Por ahora, los cambios de plan se procesan manualmente. Tras tu confirmación lo procesaremos lo antes posible. Si tienes algún problema, escribe a {email}.',
            sent: 'Enviamos tu solicitud de cambio. Te contactaremos pronto.',
            failed: 'No se pudo enviar la solicitud de cambio. Inténtalo de nuevo.',
            confirm: 'Confirmar cambio',
            unknownPlan: 'Desconocido',
            dateUnavailable: 'Fecha no disponible',
        },
    },
    upgrade: {
        eyebrow: 'Cambio de suscripción',
        headingYearly: 'Cambiar a {plan} anual',
        headingMonthly: 'Cambiar a {plan} mensual',
        intro: 'Revisa la vista previa de facturación de Paddle antes de confirmar el cambio de suscripción.',
        loading: 'Preparando la vista previa del cambio de suscripción…',
        renewalYearly: 'Renovación anual',
        renewalMonthly: 'Renovación mensual',
        credit: 'Crédito',
        payAtRenewal: 'Pagar al renovar',
        payToday: 'Pagar hoy',
        offerApplied: 'La oferta {code} se aplicará a este cambio.',
        reactivation:
            'Tu suscripción está cancelada, pero el acceso de pago sigue activo hasta el {date}. Al confirmar este cambio se reactivará la facturación y se aplicará de inmediato la mejora prorrateada.',
        reactivationPeriodEnd:
            'Tu suscripción está cancelada, pero el acceso de pago sigue activo hasta el final del período pagado. Al confirmar este cambio se reactivará la facturación y se aplicará de inmediato la mejora prorrateada.',
        scheduledNote:
            'Este cambio a un plan inferior se aplicará en tu próximo ciclo de facturación, así que tu acceso actual sigue activo hasta la renovación.',
        prorationNote:
            'Paddle calcula el importe prorrateado final según tu período de facturación actual, los impuestos, la moneda y cualquier oferta aplicable.',
        confirm: 'Confirmar cambio',
        changed: 'Se cambió tu suscripción.',
        scheduled: 'El cambio de suscripción se programó para el próximo ciclo de facturación.',
        backToProjects: 'Volver a proyectos',
        contactSupport: 'Contactar con soporte',
        errorFallback: 'No se pudo cambiar tu suscripción. Escribe a {email}',
        previewFailed: 'No se pudo preparar la vista previa del cambio de suscripción.',
        changeFailed: 'No se pudo cambiar tu suscripción.',
        cannotChangeAutomatically: 'Esta suscripción no se puede cambiar automáticamente.',
        continueWithBasic: 'Continuar con el plan Basic →',
        eligibility: {
            noActiveAnnual: 'No hay ninguna suscripción activa que se pueda cambiar a facturación anual.',
            noActive: 'No hay ninguna suscripción activa para este cambio.',
            pastDueAnnual: 'Actualiza tu método de pago antes de cambiar a facturación anual.',
            pastDue: 'Actualiza tu método de pago antes de cambiar tu suscripción.',
            legacyAnnual: 'Esta suscripción no se puede mejorar automáticamente. Contacta con soporte.',
            manualSupport: 'Esta suscripción no se puede cambiar automáticamente. Contacta con soporte.',
            planMismatch: 'Este enlace de mejora no corresponde a tu plan de suscripción actual.',
            alreadyAnnual: 'Ya tienes facturación anual.',
            unknownCycle: 'No pudimos confirmar tu ciclo de facturación actual. Contacta con soporte.',
            eligibleAnnual: 'Tu suscripción se puede cambiar a facturación anual.',
            weeklyManual: 'Los cambios del acceso semanal se gestionan manualmente. Contacta con soporte.',
            lifetimeManual: 'El acceso de por vida no usa facturación recurrente. Contacta con soporte.',
            yearlyToMonthly: 'El cambio de facturación anual a mensual se gestiona manualmente. Contacta con soporte.',
            alreadyCurrent: 'Ya tienes esta suscripción.',
            downgradeManual:
                'Los cambios a un plan inferior se gestionan manualmente para que tu acceso actual siga activo hasta la renovación.',
            eligible: 'Tu suscripción se puede cambiar de inmediato.',
        },
    },
    cancellation: {
        intro: 'Responde una breve encuesta',
        redirecting: 'Te estamos redirigiendo al portal de gestión de la suscripción…',
        step: 'Paso {current} de {total}',
        change: 'Cambiar',
        otherDetails: '¿Podrías contarnos qué exactamente?',
        otherPlaceholder: 'Cuéntanos más…',
        commentsTitle: '¿Hay algo más que debamos saber?',
        commentsHint: 'Tus comentarios nos ayudan a mejorar Lopaka para todos.',
        commentsPlaceholder: 'Detalles sobre tu experiencia, funciones que echaste en falta o cualquier otra cosa…',
        questions: {
            mainReason: '¿Cuál es el motivo principal de la cancelación?',
            contributingFactors: '¿Influyó algo más?',
            experience: '¿Cómo valoras tu experiencia en general?',
            future: '¿Qué podría convencerte de quedarte?',
        },
        options: {
            notEnoughValue: 'No veo suficiente valor en el plan de pago',
            freeTierEnough: 'El plan gratis me basta',
            switchedTool: 'Me cambié a otra herramienta',
            other: 'Otro',
            nothingElse: 'No, eso es todo',
            lowerPrice: 'Un precio más bajo',
            betterOnboarding: 'Una mejor introducción',
            moreExport: 'Más opciones de exportación',
            alreadyDecided: 'Nada, ya lo decidí',
        },
    },
    referrals: {
        closeDialog: 'Cerrar el diálogo de invitación',
        invite: {
            heading: 'Invita a un amigo',
            text: 'Gana una semana gratis de Plus cuando tu amigo haga su primera compra.',
            ticketAlt: 'Siete días de Lopaka Plus por cada amigo que pague',
            friendBenefit: 'Tu amigo recibe {extraDays} en su primera semana de Plus, {lineBreak}o {discount}.',
            extraDays: '7 días extra',
            discount: '10% de descuento en Lifetime',
            linkLabel: 'Tu enlace de invitación',
            copyLink: 'Copiar enlace de invitación',
            linkCopied: 'Enlace de invitación copiado',
            paidReferrals: 'Referidos que pagaron',
            bonusDays: 'Días extra ganados',
        },
        share: {
            heading: 'Comparte tu trabajo',
            text: 'Gana una semana gratis de Plus por cada publicación aprobada sobre Lopaka.',
            ticketAlt: 'Siete días de Lopaka Plus: una semana por publicación',
            ideas: 'Muestra tu proyecto, escribe un tutorial o un artículo, o comparte una publicación o un video. Menciona {brand} o enlaza a {site} siempre que puedas.',
            linkLabel: 'Enlace a tu publicación',
            submit: 'Enviar para revisión',
            submitted: '¡Enviado! Te escribiremos por correo cuando se apruebe.',
            alreadySubmitted: 'Esta publicación ya se envió.',
            failed: 'No se pudo enviar tu publicación. Inténtalo de nuevo.',
            reviewNote:
                'Revisamos cada enlace manualmente y te escribimos cuando se aprueba. ¿Tienes más para compartir? Envía un enlace distinto para cada publicación.',
        },
    },
    editor: {
        screenNotFound: '404: pantalla no encontrada',
        uploadingImages: 'Subiendo imágenes. Espera un momento…',
        animationTitle: 'Animación',
        grid: 'Cuadrícula',
        zoomIn: 'Acercar',
        zoomOut: 'Alejar',
        imageTooLarge: 'La imagen mide {width}×{height} píxeles; las imágenes pueden tener como máximo {max} píxeles.',
        layerDataMissing: 'Faltan los datos de la capa de imagen. No se dibujará.',
        loadingProject: 'Cargando proyecto',
        flipper: {
            liveView: 'Vista en vivo',
            disconnect: 'Desconectar',
            liveViewHint: 'Ve tu diseño en tiempo real en tu Flipper por USB',
            notFound: 'No se encontró ningún dispositivo Flipper. Conecta tu Flipper por USB e inténtalo de nuevo.',
        },
        animation: {
            preparing: 'Preparando los fotogramas de la animación',
            preparingProgress: 'Preparando los fotogramas de la animación ({completed}/{total})',
            discardChanges: '¿Descartar los cambios no guardados de la animación?',
            editingUnavailable:
                'La edición de fotogramas de animación solo está disponible en un proyecto guardado que puedas editar.',
            prepareFailed: 'No se pudo preparar la animación.',
            saveFailed: 'No se pudieron guardar los cambios de la animación.',
            checkingSave: 'Comprobando el resultado del último guardado antes de cerrar…',
            saveUnknown: 'Todavía no se conoce el resultado del último guardado.',
            createFailed: 'No se pudo crear la animación.',
            importFailed: 'No se pudo importar la animación.',
            layerLoadFailed: 'No se pudo cargar la animación "{name}".',
            pastedLoadFailed: 'No se pudo cargar la animación pegada "{name}".',
        },
        assets: {
            imagesAddFailed: 'No se pudieron agregar las imágenes.',
            imagesNotUploaded: 'No se pudo subir {count} imagen. | No se pudieron subir {count} imágenes.',
            imageAddFailed: 'No se pudo agregar la imagen.',
            animationAddFailed: 'No se pudo agregar la animación.',
            animationLoadFailed: 'No se pudo cargar la animación.',
            notFound: 'Este recurso no está disponible en el editor actual.',
            droppedImageFailed: 'No se pudo cargar la imagen soltada "{name}".',
            droppedImageRejected: 'No se pudo cargar la imagen soltada "{name}": {reason}',
        },
        codeImport: {
            noLayers: 'No se encontraron datos de capas.',
            fontNotFound: 'No se encontró la fuente {font}. Se usará la fuente predeterminada.',
            imageSkipped: 'Se omitió la imagen {name}. {reason}',
        },
        sidebar: {
            label: 'Secciones del editor',
            layers: 'Capas',
            images: 'Imágenes',
            animations: 'Animaciones',
            fonts: 'Fuentes',
            components: 'Componentes',
            prototype: 'Prototipo',
        },
        appNav: {
            projects: 'Proyectos',
            assets: 'Recursos',
        },
        publish: {
            fork: 'Copiar',
            forkHint: 'Haz una copia de este proyecto',
            forkSignInHint: 'Inicia sesión para copiar este proyecto',
            share: 'Compartir',
            copyPublicLink: 'Copiar enlace público',
            linkCopied: 'El enlace se copió al portapapeles',
            publishBoth: 'Tienes que publicar el proyecto y la pantalla',
            publishProject: 'Primero publícalo en los ajustes del proyecto',
            publishScreen: 'Primero publica la pantalla',
            presentation: 'Presentación',
            presentationUpgrade: 'Mejora a Plus para activar el modo presentación',
            saveAs: 'Guardar como',
            exportUpgrade: 'Mejora tu plan para activar la exportación de archivos',
            textFile: 'Archivo de texto',
            pngImage: 'Imagen PNG',
            bmpImage: 'Imagen BMP',
            imageSaved: 'La imagen se guardó correctamente',
            codeSaved: 'El código fuente se guardó correctamente',
        },
        presenter: {
            screenImage: 'Imagen de la pantalla',
            thumbnail: 'Miniatura de la pantalla',
            noImage: 'No hay imagen disponible',
            fullscreen: 'Pantalla completa',
            exitFullscreen: 'Salir de pantalla completa',
        },
    },
    tools: {
        undo: 'Deshacer',
        redo: 'Rehacer',
        select: 'Seleccionar',
        animation: 'Animación',
        arc: 'Arco',
        button: 'Botón',
        checkbox: 'Casilla de verificación',
        circle: 'Círculo',
        ellipse: 'Elipse',
        icon: 'Ícono',
        image: 'Imagen',
        line: 'Línea',
        paint: 'Pintar',
        panel: 'Panel',
        polygon: 'Polígono',
        rect: 'Rectángulo',
        slider: 'Control deslizante',
        switch: 'Interruptor',
        text: 'Texto',
        textArea: 'Área de texto',
        triangle: 'Triángulo',
    },
    layers: {
        emptyText: 'Texto vacío',
        unnamed: 'Capa sin nombre',
    },
    code: {
        placeholder: 'Escribe tu código aquí…',
        copySource: 'Copiar código fuente',
        copied: 'Copiado al portapapeles',
        importHint: 'Importar código (experimental): funciona mejor con código generado en Lopaka',
        syntax: 'Sintaxis',
        fonts: 'Fuentes',
        images: 'Imágenes',
        animations: 'Animaciones',
        onlyUsedGlyphs: 'Incluir solo los glifos usados',
        fontConvertFailed:
            'No se pudo convertir {font} en un encabezado de fuente U8g2. No se descargó ningún archivo.',
        settings: {
            wrap: 'Función contenedora',
            progmem: 'Declarar como PROGMEM',
            includeFonts: 'Incluir fuentes',
            includeImages: 'Declarar imágenes',
            includeImageFiles: 'Incluir archivos de imagen',
            includeAnimations: 'Funciones auxiliares de animación',
            declareVars: 'Declarar variables',
            comments: 'Comentarios, títulos de capas',
            clearScreen: 'Borrar/rellenar pantalla',
        },
    },
    shortcuts: {
        title: 'Atajos de teclado',
        move10px: 'mover 10px',
        reorderLayers: 'reordenar capas',
        copyPasteCut: 'copiar/pegar/cortar',
        selectAll: 'seleccionar todo',
        undo: 'deshacer',
        redo: 'rehacer',
        zoom: 'acercar/alejar',
        multiSelect: 'selección múltiple',
        resetSelection: 'quitar la selección',
        groupLayers: 'agrupar capas',
        ungroup: 'desagrupar',
        resizing: 'Cambiar tamaño',
        keepAspectRatio: 'Mantén {key} para conservar la proporción',
        resizeFromCenter: 'Mantén {key} para cambiar el tamaño desde el centro',
        drawLine: 'Mantén {key} para dibujar una línea',
        eraser: 'borrador',
        polygonTriangle: 'Polígono, triángulo',
        placeVertices: 'colocar vértices',
        either: '{first} o {second}',
        finish: 'terminar',
        editVertices: 'editar vértices',
        click: 'Clic',
        rightClick: 'Clic derecho',
        doubleClick: 'Doble clic',
    },
    galleryProject: {
        previewNotice:
            'Este es un proyecto público de la Galería. Para seguir editándolo, usa {fork} para guardar una copia en tus proyectos.',
        fork: 'COPIAR',
        loadFailed: 'No se pudo cargar este proyecto. Revisa tu conexión e inténtalo de nuevo.',
        screens: 'Pantallas:',
        about: {
            heading: {
                design: 'Acerca de este diseño',
                display: 'Acerca de este diseño para pantalla',
                monochrome: 'Acerca de este diseño para pantalla monocromática',
                colorTft: 'Acerca de este diseño para pantalla TFT a color',
                ePaper: 'Acerca de este diseño para pantalla e-paper',
            },
            stepsHeading: 'Cómo obtener el código {platform} de esta pantalla',
            stepsHeadingDesign: 'Cómo usar este diseño',
            summary:
                '{project} es una interfaz de usuario {platform} para una pantalla de {size} píxeles, compartida por la comunidad de Lopaka.',
            viewingScreen: 'Estás viendo la pantalla «{screen}».',
            screensCount: 'El proyecto tiene {count} pantallas; elige una en la lista Pantallas.',
            platformNote: 'Lopaka genera código {platform} para este diseño, listo para pegar en tu firmware.',
            platformNotes: {
                u8g2: 'U8g2 es una biblioteca gráfica monocromática para placas Arduino, ESP32 y STM32. Controla controladores OLED y LCD populares como SSD1306, SH1106 y ST7920, e incluye cientos de fuentes de mapa de bits compactas.',
                tftEspi:
                    'TFT_eSPI es una biblioteca gráfica rápida para pantallas TFT a color en placas ESP32, ESP8266 y RP2040. Admite controladores como ILI9341, ST7789 y ST7735, y dibuja en color RGB565 de 16 bits.',
                adafruitGfx:
                    'Adafruit GFX es la biblioteca gráfica base de la mayoría de los controladores de pantallas a color de Adafruit para Arduino. Las mismas llamadas de dibujo funcionan en ST7735, ST7789, ILI9341 y muchas otras pantallas a color.',
                adafruitGfxMono:
                    'Adafruit GFX es la biblioteca gráfica base de la mayoría de los controladores de pantalla de Adafruit para Arduino. En modo monocromático se combina con controladores como Adafruit_SSD1306 para pantallas OLED pequeñas.',
                arduinoGfx:
                    'Arduino GFX es una biblioteca de pantallas compatible con una amplia variedad de controladores a color y buses de datos, desde paneles TFT SPI hasta pantallas paralelas y RGB en placas ESP32-S3.',
                lvgl: 'LVGL es una biblioteca de GUI embebida de código abierto para microcontroladores. Ofrece widgets, estilos y animaciones para pantallas a color en ESP32, STM32 y otras placas con suficiente memoria.',
                esphome:
                    'ESPHome compila firmware para dispositivos ESP32 y ESP8266 a partir de una configuración YAML. Su componente de pantalla dibuja lambdas como esta en pantallas OLED, TFT y e-paper conectadas a Home Assistant.',
                micropython:
                    'MicroPython ejecuta Python 3 directamente en microcontroladores como el ESP32 y la Raspberry Pi Pico. Su módulo framebuf dibuja texto, formas y mapas de bits para pantallas pequeñas.',
                circuitpython:
                    'CircuitPython es una variante de MicroPython creada por Adafruit para principiantes. Su biblioteca displayio combina mapas de bits, etiquetas y formas para pantallas OLED, TFT y e-paper.',
                flipper:
                    'Las aplicaciones de Flipper Zero dibujan en su pantalla monocromática de 128×64 mediante la API Canvas del firmware de Flipper. Los diseños hechos para ella encajan exactamente en el dispositivo.',
                inkplate:
                    'Las placas Inkplate combinan un ESP32 con una pantalla e-paper reciclada. La biblioteca Inkplate para Arduino dibuja texto, formas y mapas de bits en blanco y negro o en escala de grises.',
                uint32: 'La salida uint32 sin procesar exporta el diseño como arreglos de píxeles simples, listos para un controlador de pantalla propio o cualquier framework que acepte mapas de bits sin procesar.',
                freestyle:
                    'Los proyectos de dibujo libre son pixel art sin biblioteca de destino. Copia uno para reutilizar sus capas en un proyecto para tu propia pantalla.',
                gxepd2: 'GxEPD2 es una biblioteca de Arduino para pantallas e-paper SPI de Waveshare, Good Display y otros fabricantes. Gestiona actualizaciones parciales y completas en pantallas de bajo consumo que conservan la imagen sin energía.',
            },
            codeNote:
                'Cada capa se convierte en su propia llamada de dibujo en el código {platform} exportado: el texto conserva su fuente, los íconos y las imágenes se convierten en arreglos de mapas de bits, y las formas en líneas, rectángulos y círculos, así que el código sigue siendo legible y fácil de modificar a mano.',
            codeNoteDesign:
                'Cada capa sigue siendo editable después de copiar el proyecto: el texto conserva su fuente, los íconos y las imágenes siguen siendo mapas de bits independientes, y las formas siguen siendo formas que puedes mover y redimensionar.',
            steps: {
                copyCode:
                    'Copia el código {platform} generado desde el panel de código y pégalo en tu sketch o firmware.',
                codeSettings:
                    'Ajusta la configuración del código junto al panel, por ejemplo cómo se exportan las imágenes y las fuentes.',
                star: 'Marca el proyecto con una estrella para guardarlo en la pestaña Favoritos de la Galería.',
                fork: 'Copia el proyecto para guardar tu propia copia en Mis proyectos. Para copiarlo necesitas una cuenta gratis de Lopaka.',
                edit: 'Edita el texto, las formas, los íconos y las imágenes de tu copia; el código se actualiza mientras trabajas.',
                canvas: 'Mantén el lienzo de {size} para la misma pantalla o cambia su tamaño para adaptarlo a la tuya.',
                publish: 'Publica tu versión en la Galería para compartirla con la comunidad.',
            },
            guide: 'Lee la guía de {name}',
            platformsGuide: 'Lee la guía de plataformas',
        },
    },
    imageWizard: {
        processing: {
            animationFrames: 'Procesando los fotogramas de la animación…',
            animationFramesProgress: 'Procesando los fotogramas de la animación ({completed}/{total})…',
            imageFrames: 'Procesando los fotogramas de la imagen…',
            imageFramesProgress: 'Procesando los fotogramas de la imagen ({completed}/{total})…',
            images: 'Procesando las imágenes y agregando capas al lienzo…',
        },
        sourceType: {
            uploadFile: 'Subir archivo',
            byteArray: 'Ingresar arreglo de bytes',
        },
        upload: {
            dropHint: 'Elige archivos o arrástralos y suéltalos aquí',
            browse: 'Buscar archivos',
            supportedTypes: 'Tipos de archivo compatibles',
            categories: {
                images: 'Imágenes',
                gifs: 'GIF',
                videos: 'Videos',
            },
        },
        bitmap: {
            placeholder: 'Pega aquí el arreglo de bytes. Ejemplo: 0x0A, 0x0B, 0x09',
            width: 'Ancho:',
            height: 'Alto:',
            swapBytes: 'Intercambiar bytes',
        },
        import: 'Importar',
        images: 'Imágenes',
        imagesSelected: 'Imágenes ({count})',
        preview: {
            videoTitle: 'Vista previa del video',
            videoFrameAt: 'Vista previa del video en {time}',
            gifTitle: 'Línea de tiempo de la animación',
            gifFrameAt: 'Fotograma del GIF en {time}',
        },
        metrics: {
            fps: 'Fotogramas por segundo',
            duration: 'Duración',
            frames: 'Fotogramas',
            frameSize: 'Tamaño del fotograma',
            estimatedSize: 'Tamaño estimado',
            sourceFrames: 'Fotogramas de origen',
            outputFrames: 'Fotogramas resultantes',
        },
        warnings: {
            fpsRange: 'Los fotogramas por segundo deben ser un número entero de 1 a 60.',
            videoFrameLimit:
                'Esta configuración crea {count} fotogramas. Reduce los FPS para no superar el límite de {limit} fotogramas.',
            gifFrameLimit:
                'Esta configuración crea más de {limit} fotogramas. Reduce los FPS para no superar el límite de fotogramas.',
            sizeLimit:
                'La animación estimada supera el límite de tamaño de {limit}. Reduce los FPS o las dimensiones de los fotogramas procesados.',
        },
        mode: {
            label: 'Modo de importación de archivos animados',
            frames: 'Fotogramas',
            animation: 'Animación',
            toggle: 'Importar como animación',
        },
        editor: {
            name: 'Nombre:',
            zoom: 'Zoom:',
            monochrome: 'Monocromo',
            rgb: 'RGB',
            byteSize: '{size} bytes',
            dimensions: 'Dimensiones',
            widthShort: 'An',
            heightShort: 'Al',
            fitScreen: 'Ajustar a la pantalla',
            cropArea: 'Área de recorte',
            brightness: 'Brillo',
            contrast: 'Contraste',
            grayscaleFirst: 'Escala de grises primero',
            invertPalette: 'Invertir paleta',
            invertResult: 'Invertir resultado',
            resampling: {
                label: 'Remuestreo',
                highQuality: 'Alta calidad',
                bilinear: 'Bilineal',
                nearest: 'Más cercano',
            },
            dithering: {
                label: 'Tramado',
                floydSteinberg: 'Floyd-Steinberg',
                atkinson: 'Atkinson',
                bayer: 'Bayer 8x8',
                none: 'Sin tramado',
            },
        },
        errors: {
            loadFailed: 'No se pudieron cargar las imágenes. Revisa los archivos e inténtalo de nuevo.',
            processFailed: 'No se pudieron procesar las imágenes. Inténtalo de nuevo.',
            videoProcessFailed: 'No se pudo procesar el video. Inténtalo de nuevo o elige otro video.',
            removeFailed:
                'No se pudo quitar la imagen del almacenamiento del proyecto. Actualiza la página para reintentar.',
        },
    },
    icons: {
        currentProject: 'Proyecto actual',
        upgradeBanner: 'Mejora tu plan para acceder a todos los paquetes de íconos',
        upgradeHint: 'Mejora tu plan para acceder a todos los íconos',
        noCustomImages: 'Este proyecto no tiene imágenes ni íconos propios',
        searchLabel: 'Buscar íconos por nombre',
        clearSearch: 'Borrar la búsqueda de íconos',
        noResults: 'No se encontraron íconos',
        seeMore: 'Ver más →',
    },
    animations: {
        categories: {
            ui: 'Interfaz',
            web: 'Web',
            social: 'Social',
            media: 'Multimedia',
            weather: 'Clima',
            ecommerce: 'E-commerce',
            loaders: 'Cargadores',
            emoji: 'Emoji',
            gestures: 'Gestos',
            food: 'Comida',
            sports: 'Deportes',
            custom: 'Personalizadas',
        },
        detail: {
            high: 'Alta',
            medium: 'Media',
            low: 'Baja',
        },
        frameCount: '{count} fotograma | {count} fotogramas',
        upgradeTooltip: 'Mejora tu plan para tener acceso completo',
        deleteCustomWarning:
            '¿Eliminar esta animación personalizada? Todas las capas del proyecto actual que la usan dejarán de estar disponibles.',
        searchLabel: 'Buscar animaciones por nombre',
        clearSearch: 'Borrar la búsqueda',
        createHint: 'Agregar una animación personalizada',
        createNew: 'Crear nueva',
        noResults: 'No se encontraron animaciones',
        custom: {
            loading: 'Cargando las animaciones guardadas…',
            loadFailed:
                'Las animaciones guardadas no están disponibles. Vuelve a seleccionar Personalizadas para reintentar.',
            empty: 'No hay animaciones guardadas',
            use: 'Usar la animación guardada {id}',
            unavailableLabel: 'Animación guardada no disponible',
            unavailable: 'No disponible',
        },
    },
    assets: {
        title: 'Recursos',
        stats: {
            total: 'Total',
            images: 'Imágenes',
            fonts: 'Fuentes',
            remaining: 'Disponibles',
            unlimited: 'Ilimitados',
        },
        upgrade: 'Mejora tu plan para agregar más',
        empty: 'Aún no has subido recursos.',
        emptyHint: 'Sube imágenes y fuentes desde el editor para verlas aquí.',
        count: '{count} recurso | {count} recursos',
        delete: 'Eliminar {name}',
        untitledProject: 'Proyecto {id}',
    },
    docsSite: {
        openApp: 'Abrir la app de Lopaka',
        sidebar: {
            documentation: 'Documentación',
            overview: 'Descripción general',
            gettingStarted: 'Primeros pasos',
            platforms: 'Plataformas',
            editorOverview: 'Resumen del editor',
            importingImages: 'Importar imágenes',
            animations: 'Animaciones',
            fonts: 'Fuentes',
            codeGeneration: 'Generación de código',
            keyboardShortcuts: 'Atajos de teclado',
            subscription: 'Suscripción',
            plansDescription: 'Descripción de los planes',
            platformGuides: 'Guías de plataformas',
        },
        outlineLabel: 'En esta página',
        previousPage: 'Página anterior',
        nextPage: 'Página siguiente',
        langMenuLabel: 'Cambiar idioma',
        sidebarMenuLabel: 'Menú',
        returnToTopLabel: 'Volver arriba',
        darkModeSwitchLabel: 'Apariencia',
        lightModeSwitchTitle: 'Cambiar al tema claro',
        darkModeSwitchTitle: 'Cambiar al tema oscuro',
        skipToContentLabel: 'Ir al contenido',
        notFound: {
            title: 'PÁGINA NO ENCONTRADA',
            quote: 'Pero si no cambias de dirección y sigues buscando, puede que llegues a donde te diriges.',
            linkLabel: 'ir al inicio',
            linkText: 'Volver al inicio',
        },
        search: {
            buttonText: 'Buscar',
            displayDetails: 'Mostrar lista detallada',
            resetButtonTitle: 'Restablecer búsqueda',
            backButtonTitle: 'Cerrar búsqueda',
            noResultsText: 'Sin resultados para',
            selectText: 'para seleccionar',
            selectKeyAriaLabel: 'intro',
            navigateText: 'para navegar',
            navigateUpKeyAriaLabel: 'flecha arriba',
            navigateDownKeyAriaLabel: 'flecha abajo',
            closeText: 'para cerrar',
            closeKeyAriaLabel: 'escape',
        },
    },
};

export default es;
